import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  PaginatedResponse, 
  AuthResponse,
  User,
  Project,
  Issue,
  Sprint,
  Team,
  Board,
  Release,
  DashboardOverview,
  ProjectMetrics,
  BurndownPoint,
  CreateProjectData,
  CreateIssueData,
  CreateSprintData,
  IssueFilters,
  Comment,
  Leave,
  Timesheet,
  TimesheetApprovalHistory,
  Epic,
  CreateEpicData
} from '../types';
import {
  StoryGenerationJob,
  GeneratedStory,
  EstimationConfig,
  UploadDocumentRequest,
  ReviewStoryRequest,
  StoryStatus
} from '../types/estimation';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      console.log('🔧 API Request:', config.method?.toUpperCase(), config.url);
      console.log('🔧 Token present:', !!token);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
        return response;
      },
      (error) => {
        console.error('❌ API Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status);
        console.error('❌ Error details:', error.response?.data || error.message);
        if (error.response?.status === 401) {
          console.log('🚨 401 Unauthorized - removing token and redirecting');
          localStorage.removeItem('authToken');
          window.location.href = '/auth';
        }
        // Avoid unhandled promise rejections causing UI stalls
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/login', {
      email,
      password,
    });
    return response.data.data!;
  }

  async register(userData: any): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/register', userData);
    return response.data.data!;
  }

  async registerOrganization(data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/register-organization', data);
    return response.data.data!;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/auth/me');
    return response.data.data!;
  }

  // Generic GET method
  async get<T = any>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.get(url);
    return response.data;
  }

  // Generic POST method
  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.post(url, data);
    return response.data;
  }

  // Organization endpoints (Root User only)
  async getOrganizations(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/organizations');
    return response.data.data!;
  }

  // Analytics endpoints
  async getAllSprints(): Promise<Sprint[]> {
    const response: AxiosResponse<ApiResponse<Sprint[]>> = await this.api.get('/sprints');
    return response.data.data!;
  }

  async createOrganization(data: { name: string; description?: string; website?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/organizations', data);
    return response.data.data!;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put('/auth/me', data);
    return response.data.data!;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/auth/change-password', { currentPassword, newPassword });
  }

  async refreshToken(): Promise<{ token: string; expiresIn: string }> {
    const response: AxiosResponse<ApiResponse<{ token: string; expiresIn: string }>> = await this.api.post('/auth/refresh');
    return response.data.data!;
  }

  // Project endpoints
  async getProjects(organizationId?: string): Promise<Project[]> {
    const url = organizationId ? `/projects?organizationId=${organizationId}` : '/projects';
    const response: AxiosResponse<ApiResponse<Project[]>> = await this.api.get(url);
    return response.data.data || [];
  }

  async getUserProjects(): Promise<Project[]> {
    const response: AxiosResponse<ApiResponse<Project[]>> = await this.api.get('/user-projects');
    return response.data.data!;
  }

  async getProject(id: string): Promise<Project> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.get(`/projects/${id}`);
    return response.data.data!;
  }

  async createProject(data: CreateProjectData): Promise<Project> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.post('/projects', data);
    return response.data.data!;
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    const response: AxiosResponse<ApiResponse<Project>> = await this.api.put(`/projects/${id}`, data);
    return response.data.data!;
  }

  async deleteProject(id: string): Promise<void> {
    await this.api.delete(`/projects/${id}`);
  }

  async getProjectMetrics(id: string): Promise<ProjectMetrics> {
    const response: AxiosResponse<ApiResponse<ProjectMetrics>> = await this.api.get(`/projects/${id}/metrics`);
    return response.data.data!;
  }

  // Issue endpoints
  async getIssues(filters: IssueFilters): Promise<PaginatedResponse<Issue[]>> {
    // Convert array parameters to comma-separated strings for backend compatibility
    const params: any = { ...filters };
    if (params.type && Array.isArray(params.type)) {
      params.type = params.type.join(',');
    }
    if (params.status && Array.isArray(params.status)) {
      params.status = params.status.join(',');
    }
    if (params.priority && Array.isArray(params.priority)) {
      params.priority = params.priority.join(',');
    }
    if (params.labels && Array.isArray(params.labels)) {
      params.labels = params.labels.join(',');
    }
    
    const response: AxiosResponse<PaginatedResponse<Issue[]>> = await this.api.get('/issues', { params });
    return response.data;
  }

  async getIssue(id: string): Promise<Issue> {
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.get(`/issues/${id}`);
    return response.data.data!;
  }

  async createIssue(data: CreateIssueData): Promise<Issue> {
    // Clean up the data before sending to avoid backend errors with empty strings
    const cleanedData: any = { ...data };
    
    // Remove empty string fields - they should be undefined/null instead
    if (cleanedData.assigneeId === '') delete cleanedData.assigneeId;
    if (cleanedData.epicId === '') delete cleanedData.epicId;
    if (cleanedData.sprintId === '') delete cleanedData.sprintId;
    if (cleanedData.dueDate === '') delete cleanedData.dueDate;
    if (cleanedData.parentId === '') delete cleanedData.parentId;
    
    // Remove empty arrays
    if (cleanedData.labels && Array.isArray(cleanedData.labels) && cleanedData.labels.length === 0) {
      delete cleanedData.labels;
    }
    
    // Remove empty objects
    if (cleanedData.customFields && typeof cleanedData.customFields === 'object' && Object.keys(cleanedData.customFields).length === 0) {
      delete cleanedData.customFields;
    }
    
    console.log('Original data:', data);
    console.log('Cleaned data:', cleanedData);
    
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.post('/issues', cleanedData);
    
    // Notify other pages that a story was created
    const storyCreatedEvent = {
      type: 'STORY_CREATED',
      projectId: cleanedData.projectId,
      timestamp: Date.now()
    };
    console.log('API: Broadcasting story creation event:', storyCreatedEvent);
    
    localStorage.setItem('storyCreatedEvent', JSON.stringify(storyCreatedEvent));
    console.log('API: Set localStorage event');
    
    // Also dispatch a custom event for same-tab communication
    console.log('API: Dispatching custom event');
    window.dispatchEvent(new CustomEvent('storyCreated', { 
      detail: storyCreatedEvent 
    }));
    console.log('API: Custom event dispatched');
    
    return response.data.data!;
  }

  async getEpics(projectId: string): Promise<Epic[]> {
    const response: AxiosResponse<ApiResponse<Epic[]>> = await this.api.get(`/epics/project/${projectId}`);
    return response.data.data!;
  }

  async getEpic(id: string): Promise<Epic> {
    const response: AxiosResponse<ApiResponse<Epic>> = await this.api.get(`/epics/${id}`);
    return response.data.data!;
  }

  async createEpic(data: CreateEpicData): Promise<Epic> {
    const response: AxiosResponse<ApiResponse<Epic>> = await this.api.post('/epics', data);
    return response.data.data!;
  }

  async updateEpic(id: string, data: Partial<Epic>): Promise<Epic> {
    const response: AxiosResponse<ApiResponse<Epic>> = await this.api.put(`/epics/${id}`, data);
    return response.data.data!;
  }

  async deleteEpic(id: string): Promise<void> {
    await this.api.delete(`/epics/${id}`);
  }

  // Sprint Epic Management
  async addEpicsToSprint(sprintId: string, epicIds: string[]): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.post(`/sprints/${sprintId}/epics`, { epicIds });
    return response.data.data!;
  }

  async removeEpicsFromSprint(sprintId: string, epicIds: string[]): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.delete(`/sprints/${sprintId}/epics`, { data: { epicIds } });
    return response.data.data!;
  }

  async getSprintWithEpics(sprintId: string): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.get(`/sprints/${sprintId}/with-epics`);
    return response.data.data!;
  }

  async updateIssue(id: string, data: Partial<Issue>): Promise<Issue> {
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.put(`/issues/${id}`, data);
    return response.data.data!;
  }

  async deleteIssue(id: string): Promise<void> {
    await this.api.delete(`/issues/${id}`);
  }

  async addComment(issueId: string, content: string, isInternal = false): Promise<Comment> {
    const response: AxiosResponse<ApiResponse<Comment>> = await this.api.post(`/issues/${issueId}/comments`, {
      content,
      isInternal,
    });
    return response.data.data!;
  }

  async getIssueComments(issueId: string): Promise<Comment[]> {
    const response: AxiosResponse<ApiResponse<Comment[]>> = await this.api.get(`/issues/${issueId}/comments`);
    return response.data.data || [];
  }

  async getIssueActivity(issueId: string): Promise<Comment[]> {
    const response: AxiosResponse<ApiResponse<Comment[]>> = await this.api.get(`/issues/${issueId}/activity`);
    return response.data.data || [];
  }

  async logTime(issueId: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/issues/${issueId}/time-logs`, data);
    return response.data.data!;
  }

  async reorderIssues(projectId: string, issueOrders: Array<{ id: string; orderIndex: number }>): Promise<void> {
    await this.api.put('/issues/reorder', { projectId, issueOrders });
  }

  // Sprint endpoints
  async getSprints(projectId: string, includeCompleted = false): Promise<Sprint[]> {
    const response: AxiosResponse<ApiResponse<Sprint[]>> = await this.api.get(`/sprints/project/${projectId}`, {
      params: { includeCompleted },
    });
    return response.data.data!;
  }

  async getProjectSprints(projectId: string, includeCompleted = false): Promise<Sprint[]> {
    const response: AxiosResponse<ApiResponse<Sprint[]>> = await this.api.get(`/sprints/project/${projectId}/with-boards`, {
      params: { includeCompleted },
    });
    return response.data.data!;
  }

  async getSprint(id: string): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.get(`/sprints/${id}`);
    return response.data.data!;
  }

  async createSprint(data: CreateSprintData): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.post('/sprints', data);
    return response.data.data!;
  }

  async updateSprint(id: string, data: Partial<Sprint>): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.put(`/sprints/${id}`, data);
    return response.data.data!;
  }

  async startSprint(id: string): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.post(`/sprints/${id}/start`);
    return response.data.data!;
  }

  async completeSprint(id: string): Promise<Sprint> {
    const response: AxiosResponse<ApiResponse<Sprint>> = await this.api.post(`/sprints/${id}/complete`);
    return response.data.data!;
  }

  async addIssueToSprint(sprintId: string, issueId: string): Promise<Issue> {
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.post(`/sprints/${sprintId}/issues/${issueId}`);
    return response.data.data!;
  }

  async removeIssueFromSprint(sprintId: string, issueId: string): Promise<Issue> {
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.delete(`/sprints/${sprintId}/issues/${issueId}`);
    return response.data.data!;
  }

  async getSprintBurndown(id: string): Promise<BurndownPoint[]> {
    const response: AxiosResponse<ApiResponse<BurndownPoint[]>> = await this.api.get(`/sprints/${id}/burndown`);
    return response.data.data!;
  }

  // Board endpoints
  async getBoard(projectId: string, boardId?: string): Promise<{ board: Board; issuesByStatus: Record<string, Issue[]>; totalIssues: number }> {
    const response: AxiosResponse<ApiResponse<{ board: Board; issuesByStatus: Record<string, Issue[]>; totalIssues: number }>> = await this.api.get(`/boards/project/${projectId}`, {
      params: { boardId },
    });
    return response.data.data!;
  }

  // Export board issues as CSV
  async exportBoardIssues(boardId: string): Promise<Blob> {
    const response = await this.api.get(`/boards/${boardId}/export?format=csv`, { responseType: 'blob' as any });
    return response.data as any;
  }

  async updateBoardRagSettings(boardId: string, settings: { amberThresholdDays: number; redGracePeriodDays: number; enabled?: boolean }): Promise<Board> {
    const response: AxiosResponse<ApiResponse<Board>> = await this.api.put(`/boards/${boardId}/rag-settings`, settings);
    return response.data.data!;
  }

  async getBoardsForProject(projectId: string): Promise<Board[]> {
    const response: AxiosResponse<ApiResponse<Board[]>> = await this.api.get(`/boards/project/${projectId}/all`);
    return response.data.data!;
  }

  async createBoard(data: any): Promise<Board> {
    const response: AxiosResponse<ApiResponse<Board>> = await this.api.post('/boards', data);
    return response.data.data!;
  }

  async updateIssueStatus(issueId: string, newStatus: string, newOrderIndex?: number): Promise<Issue> {
    const response: AxiosResponse<ApiResponse<Issue>> = await this.api.put(`/boards/issues/${issueId}/status`, {
      newStatus,
      newOrderIndex,
    });
    return response.data.data!;
  }

  // Team endpoints
  async getTeams(projectId: string): Promise<Team[]> {
    const response: AxiosResponse<ApiResponse<Team[]>> = await this.api.get(`/teams/project/${projectId}`);
    return response.data.data!;
  }

  async createTeam(data: any): Promise<Team> {
    const response: AxiosResponse<ApiResponse<Team>> = await this.api.post('/teams', data);
    return response.data.data!;
  }

  async updateTeam(teamId: string, data: any): Promise<Team> {
    const response: AxiosResponse<ApiResponse<Team>> = await this.api.put(`/teams/${teamId}`, data);
    return response.data.data!;
  }

  async deleteTeam(teamId: string): Promise<void> {
    await this.api.delete(`/teams/${teamId}`);
  }

  async addTeamMember(teamId: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/teams/${teamId}/members`, data);
    return response.data.data!;
  }

  async getProjectTeamMembers(projectId: string): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get(`/teams/project/${projectId}/members`);
    return response.data.data!;
  }



  // Release endpoints
  async getReleases(projectId: string): Promise<Release[]> {
    const response: AxiosResponse<ApiResponse<Release[]>> = await this.api.get(`/releases/project/${projectId}`);
    return response.data.data!;
  }

  async createRelease(data: any): Promise<Release> {
    const response: AxiosResponse<ApiResponse<Release>> = await this.api.post('/releases', data);
    return response.data.data!;
  }

  // Dashboard endpoints
  async getDashboardOverview(): Promise<DashboardOverview> {
    const response: AxiosResponse<ApiResponse<DashboardOverview>> = await this.api.get('/dashboard/overview');
    return response.data.data!;
  }

  async getTeamPerformance(projectId: string, timeframe = '30'): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/dashboard/team-performance', {
      params: { projectId, timeframe },
    });
    return response.data.data!;
  }

  async getIssueDistribution(projectId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/dashboard/issue-distribution', {
      params: { projectId },
    });
    return response.data.data!;
  }

  // Integration endpoints
  async syncUsers(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/integration/sync/users');
    return response.data.data!;
  }

  async syncProjects(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/integration/sync/projects');
    return response.data.data!;
  }

  async importTimeLogs(projectId: string, startDate: string, endDate: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/integration/import/time-logs', {
      projectId,
      startDate,
      endDate,
    });
    return response.data.data!;
  }

  async getIntegrationStatus(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/integration/status');
    return response.data.data!;
  }

  // Leave endpoints
  async getUserLeaves(filters?: any): Promise<Leave[]> {
    try {
      const response: AxiosResponse<Leave[]> = await this.api.get('/leaves', {
        params: filters,
      });
      // Ensure we always return an array, even if the API response is unexpected
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching leaves:', error);
      return []; // Return empty array on error
    }
  }

  async getLeaveById(id: string): Promise<Leave> {
    const response: AxiosResponse<Leave> = await this.api.get(`/leaves/${id}`);
    return response.data;
  }

  async createLeave(data: any): Promise<Leave> {
    const response: AxiosResponse<Leave> = await this.api.post('/leaves', data);
    return response.data;
  }

  async updateLeave(id: string, data: any): Promise<Leave> {
    const response: AxiosResponse<Leave> = await this.api.put(`/leaves/${id}`, data);
    return response.data;
  }

  async approveLeave(id: string): Promise<Leave> {
    const response: AxiosResponse<Leave> = await this.api.post(`/leaves/${id}/approve`);
    return response.data;
  }

  async rejectLeave(id: string, reason?: string): Promise<Leave> {
    const response: AxiosResponse<Leave> = await this.api.post(`/leaves/${id}/reject`, { reason });
    return response.data;
  }

  async deleteLeave(id: string): Promise<void> {
    await this.api.delete(`/leaves/${id}`);
  }

  async getPendingLeaves(): Promise<Leave[]> {
    const response: AxiosResponse<Leave[]> = await this.api.get('/leaves/pending');
    return response.data;
  }

  async getAllLeaves(filters?: any): Promise<Leave[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    
    const response: AxiosResponse<Leave[]> = await this.api.get(`/leaves/all?${params.toString()}`);
    return response.data;
  }

  // Timesheet endpoints
  async getTimesheets(filters?: any): Promise<Timesheet[]> {
    const response: AxiosResponse<ApiResponse<Timesheet[]>> = await this.api.get('/timesheets', {
      params: filters,
    });
    return response.data.data!;
  }

  async getTimesheetById(id: string): Promise<Timesheet> {
    const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.get(`/timesheets/${id}`);
    return response.data.data!;
  }

  async getTimesheetByWeek(fromDate: string, toDate: string): Promise<Timesheet | null> {
    try {
      const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.get(`/timesheets/week/${fromDate}/${toDate}`);
      return response.data.data!;
    } catch (error) {
      // If no timesheet found for this week, return null
      return null;
    }
  }

  async createTimesheet(data: any): Promise<{ id: string }> {
    const response: AxiosResponse<ApiResponse<{ id: string }>> = await this.api.post('/timesheets', data);
    return response.data.data!;
  }

  async updateTimesheet(id: string, data: any): Promise<Timesheet> {
    const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.put(`/timesheets/${id}`, data);
    return response.data.data!;
  }

  async submitTimesheet(id: string): Promise<Timesheet> {
    const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.post(`/timesheets/${id}/submit`);
    return response.data.data!;
  }

  async approveTimesheet(id: string, comments?: string): Promise<Timesheet> {
    const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.post(`/timesheets/${id}/approve`, { comments });
    return response.data.data!;
  }

  async rejectTimesheet(id: string, reason: string, comments?: string): Promise<Timesheet> {
    const response: AxiosResponse<ApiResponse<Timesheet>> = await this.api.post(`/timesheets/${id}/reject`, { reason, comments });
    return response.data.data!;
  }

  async getPendingTimesheetsForApproval(): Promise<Timesheet[]> {
    const response: AxiosResponse<ApiResponse<Timesheet[]>> = await this.api.get('/timesheets/pending/approval');
    return response.data.data!;
  }

  async getPendingTimesheets(): Promise<Timesheet[]> {
    const response: AxiosResponse<ApiResponse<Timesheet[]>> = await this.api.get('/timesheets/pending');
    return response.data.data!;
  }

  async getUserTimesheets(filters?: any): Promise<Timesheet[]> {
    const response: AxiosResponse<ApiResponse<Timesheet[]>> = await this.api.get('/timesheets', {
      params: filters,
    });
    return response.data.data!;
  }

  async getTimesheetApprovalHistory(id: string): Promise<TimesheetApprovalHistory[]> {
    const response: AxiosResponse<ApiResponse<TimesheetApprovalHistory[]>> = await this.api.get(`/timesheets/${id}/history`);
    return response.data.data!;
  }

  async getManagerApprovalHistory(): Promise<TimesheetApprovalHistory[]> {
    const response: AxiosResponse<ApiResponse<TimesheetApprovalHistory[]>> = await this.api.get('/timesheets/manager/approval-history');
    return response.data.data!;
  }

  // Additional User endpoints
  async getAllUsers(): Promise<User[]> {
    const response: AxiosResponse<User[]> = await this.api.get('/users');
    return response.data || [];
  }

  // Leadership Analytics API methods
  async getLeadershipDashboard(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/leadership-analytics/dashboard');
    return response.data.data;
  }

  async getUserPerformanceMetrics(userId: string, dateRange?: { start: Date; end: Date }): Promise<any> {
    const params = dateRange ? {
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString()
    } : {};
    const response: AxiosResponse<any> = await this.api.get(`/leadership-analytics/users/${userId}/performance`, { params });
    return response.data.data;
  }

  async getUsersPerformanceSummary(dateRange?: { start: Date; end: Date }, limit: number = 10): Promise<any[]> {
    const params = {
      limit: limit.toString(),
      ...(dateRange && {
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString()
      })
    };
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/leadership-analytics/users/performance-summary', { params });
    return response.data.data || [];
  }

  async getSprintPerformanceMetrics(sprintId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/leadership-analytics/sprints/${sprintId}/performance`);
    return response.data.data;
  }

  async getSprintsPerformanceSummary(projectId?: string, status?: string, limit: number = 20): Promise<any[]> {
    const params = {
      limit: limit.toString(),
      ...(projectId && { projectId }),
      ...(status && { status })
    };
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/leadership-analytics/sprints/performance-summary', { params });
    return response.data.data || [];
  }

  async getProjectPerformanceMetrics(projectId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/leadership-analytics/projects/${projectId}/performance`);
    return response.data.data;
  }

  async getProjectsPerformanceSummary(status?: string, methodology?: string, limit: number = 20): Promise<any[]> {
    const params = {
      limit: limit.toString(),
      ...(status && { status }),
      ...(methodology && { methodology })
    };
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/leadership-analytics/projects/performance-summary', { params });
    return response.data.data || [];
  }

  async getRiskAssessment(level: string = 'all'): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/leadership-analytics/risk-assessment', {
      params: { level }
    });
    return response.data.data;
  }

  async getTrendsAnalysis(period: number = 30, metric: string = 'all'): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/leadership-analytics/trends', {
      params: { period: period.toString(), metric }
    });
    return response.data.data;
  }

  async getUsersByOrganization(organizationId: string): Promise<User[]> {
    const response: AxiosResponse<User[]> = await this.api.get(`/users/organization/${organizationId}`);
    return response.data || [];
  }

  async createUser(data: any): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/users', data);
    return response.data.data!;
  }

  async updateUser(id: string, data: any): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/users/${id}`, data);
    return response.data.data!;
  }

  async updateUserRole(id: string, role: string): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/users/${id}/role`, { role });
    return response.data.data!;
  }

  async updateUserStatus(id: string, isActive: boolean): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/users/${id}/activate`, { isActive });
    return response.data.data!;
  }

  async resetPassword(id: string, newPassword: string, mustChangePassword: boolean = false): Promise<void> {
    await this.api.put(`/users/${id}/reset-password`, { 
      newPassword, 
      mustChangePassword 
    });
  }

  // Admin Tasks API methods
  async getAdminTasksProjects(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin-tasks/projects');
    return response.data.data || [];
  }

  async createAdminTasksProject(projectData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin-tasks/projects', projectData);
    return response.data.data;
  }

  async syncProjectsFromTyvraPM(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post('/admin-tasks/sync-from-agile-pm');
    return response.data.data || [];
  }

  async getProjectTimeEntries(projectId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/admin-tasks/projects/${projectId}/time-entries`);
    return response.data.data || [];
  }

  async createTimeEntry(timeEntryData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin-tasks/time-entries', timeEntryData);
    return response.data.data;
  }

  async getProjectStats(projectId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin-tasks/projects/${projectId}/stats`);
    return response.data.data;
  }

  // Employee endpoints
  async getEmployees(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/employees');
    return response.data.data || [];
  }

  async createEmployee(employeeData: any, organizationId?: string): Promise<any> {
    const params = organizationId ? { organizationId } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/employees', employeeData, { params });
    return response.data.data!;
  }

  async updateEmployee(id: string, employeeData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/employees/${id}`, employeeData);
    return response.data.data!;
  }

  async deleteEmployee(id: string): Promise<void> {
    await this.api.delete(`/employees/${id}`);
  }

  async syncEmployeesFromTyvraPM(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post('/employees/sync-from-agile-pm');
    return response.data.data || [];
  }

  // Project Assignment endpoints
  async assignEmployeeToProject(userId: string, projectId: string, role: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/project-assignments/assign', {
      userId,
      projectId,
      role
    });
    return response.data.data!;
  }

  async removeEmployeeFromProject(userId: string, projectId: string): Promise<void> {
    await this.api.delete('/project-assignments/remove', {
      data: { userId, projectId }
    });
  }

  async getProjectAssignments(projectId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/project-assignments/project/${projectId}`);
    return response.data.data!;
  }

  async getAllProjectAssignments(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/project-assignments');
    return response.data.data!;
  }

  async getEmployeeAssignments(userId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/project-assignments/employee/${userId}`);
    return response.data.data!;
  }

  async updateEmployeeRole(userId: string, projectId: string, role: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/project-assignments/update-role', {
      userId,
      projectId,
      role
    });
    return response.data.data!;
  }

  async getAvailableEmployees(): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get('/project-assignments/available-employees');
    return response.data.data!;
  }

  async getAvailableProjects(): Promise<Project[]> {
    const response: AxiosResponse<ApiResponse<Project[]>> = await this.api.get('/project-assignments/available-projects');
    return response.data.data!;
  }

  // Board Member methods
  async assignMemberToBoard(userId: string, boardId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/board-members/assign', {
      userId,
      boardId
    });
    return response.data.data!;
  }

  async removeMemberFromBoard(userId: string, boardId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete('/board-members/remove', {
      data: { userId, boardId }
    });
    return response.data.data!;
  }

  async getBoardMembers(boardId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/board-members/board/${boardId}`);
    return response.data.data!;
  }

  async getAvailableMembersForBoard(boardId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/board-members/available/${boardId}`);
    return response.data.data!;
  }

  // Sprint Management methods
  async assignSprintToBoard(sprintId: string, boardId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/boards/assign-sprint', {
      sprintId,
      boardId
    });
    return response.data.data!;
  }

  async getBoardSprints(boardId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/boards/${boardId}/sprints`);
    return response.data.data!;
  }

  async getActiveSprint(boardId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/boards/${boardId}/active-sprint`);
    return response.data.data!;
  }

  async getSprintMetrics(sprintId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/sprints/${sprintId}/metrics`);
    return response.data.data!;
  }

  async getAvailableSprints(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/sprints/available');
    return response.data.data!;
  }

  // AI endpoints
  async generateStoryWithAI(payload: { title: string; type?: string; projectName?: string; epicName?: string; sprintName?: string; hint?: string; }): Promise<{ description: string; acceptanceCriteria: string[] }> {
    const response: AxiosResponse<ApiResponse<{ description: string; acceptanceCriteria: string[] }>> = await this.api.post('/ai/generate-story', payload);
    return response.data.data!;
  }

  // Import issues
  async importIssues(projectId: string, provider: string, file: File): Promise<Issue[]> {
    const form = new FormData();
    form.append('file', file);
    form.append('projectId', projectId);
    form.append('provider', provider);
    const response: AxiosResponse<ApiResponse<Issue[]>> = await this.api.post('/issues/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data!;
  }

  // Column management methods
  async addColumn(boardId: string, data: {
    name: string;
    status?: string; // Make status optional since backend will auto-generate it
    wipLimit?: number;
    color?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/boards/${boardId}/columns`, data);
    return response.data.data!;
  }

  async updateColumn(columnId: string, data: Partial<{
    name: string;
    status: string;
    wipLimit: number;
    color: string;
    isCollapsed: boolean;
  }>): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/boards/columns/${columnId}`, data);
    return response.data.data!;
  }

  async deleteColumn(columnId: string): Promise<void> {
    await this.api.delete(`/boards/columns/${columnId}`);
  }

  async reorderColumns(boardId: string, columnOrders: Array<{ id: string; orderIndex: number }>): Promise<void> {
    await this.api.put(`/boards/${boardId}/columns/reorder`, { columnOrders });
  }

  // Issue Assignment endpoints
  async assignUsersToIssue(issueId: string, userIds: string[], role?: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post(`/issue-assignments/${issueId}/assign`, {
      userIds,
      role
    });
    return response.data.data!;
  }

  async removeUserFromIssue(issueId: string, userId: string): Promise<void> {
    await this.api.delete(`/issue-assignments/${issueId}/assign/${userId}`);
  }

  async getIssueAssignments(issueId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/issue-assignments/${issueId}`);
    return response.data.data!;
  }

  async getUserAssignments(userId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/issue-assignments/user/${userId}`);
    return response.data.data!;
  }

  async updateAssignmentRole(issueId: string, userId: string, role: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/issue-assignments/${issueId}/assign/${userId}/role`, {
      role
    });
    return response.data.data!;
  }

  // Attachment endpoints
  async getIssueAttachments(issueId: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/attachments/issue/${issueId}`);
    return response.data.data!;
  }

  async uploadAttachment(issueId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    console.log('Sending upload request to:', `/attachments/upload/${issueId}`);
    console.log('File details:', file.name, file.size, file.type);

    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/attachments/upload/${issueId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Upload response:', response.data);
    return response.data.data!;
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    await this.api.delete(`/attachments/${attachmentId}`);
  }

  async getAttachment(attachmentId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/attachments/${attachmentId}`);
    return response.data.data!;
  }

  // Estimation Tool endpoints
  async createEstimationJob(data: { name: string; description?: string; projectId?: string }): Promise<StoryGenerationJob> {
    const response: AxiosResponse<ApiResponse<StoryGenerationJob>> = await this.api.post('/estimation/create-job', data);
    return response.data.data!;
  }

  async uploadDocument(data: UploadDocumentRequest): Promise<StoryGenerationJob> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    if (data.projectId) formData.append('projectId', data.projectId);

    const response: AxiosResponse<ApiResponse<StoryGenerationJob>> = await this.api.post('/estimation/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data!;
  }

  async getStoryGenerationJobs(projectId?: string, status?: string): Promise<StoryGenerationJob[]> {
    const params: any = {};
    if (projectId) params.projectId = projectId;
    if (status) params.status = status;

    const response: AxiosResponse<ApiResponse<StoryGenerationJob[]>> = await this.api.get('/estimation/jobs', { params });
    return response.data.data!;
  }

  async getStoryGenerationJob(jobId: string): Promise<{ job: StoryGenerationJob; stories: GeneratedStory[] }> {
    const response: AxiosResponse<ApiResponse<{ job: StoryGenerationJob; stories: GeneratedStory[] }>> = await this.api.get(`/estimation/jobs/${jobId}`);
    return response.data.data!;
  }

           async reviewStory(storyId: string, data: ReviewStoryRequest): Promise<GeneratedStory> {
           const response: AxiosResponse<ApiResponse<GeneratedStory>> = await this.api.put(`/estimation/stories/${storyId}/review`, data);
           return response.data.data!;
         }

         async updateGeneratedStory(storyId: string, data: Partial<GeneratedStory>): Promise<GeneratedStory> {
           const response: AxiosResponse<ApiResponse<GeneratedStory>> = await this.api.put(`/estimation/stories/${storyId}`, data);
           return response.data.data!;
         }

         async createGeneratedStory(jobId: string, data: Partial<GeneratedStory>): Promise<GeneratedStory> {
           const response: AxiosResponse<ApiResponse<GeneratedStory>> = await this.api.post('/estimation/stories', {
             jobId,
             ...data
           });
           return response.data.data!;
         }

  async getEstimationConfigs(projectId?: string): Promise<EstimationConfig[]> {
    const params: any = {};
    if (projectId) params.projectId = projectId;

    const response: AxiosResponse<ApiResponse<EstimationConfig[]>> = await this.api.get('/estimation/configs', { params });
    return response.data.data!;
  }

           async createEstimationConfig(data: Partial<EstimationConfig>): Promise<EstimationConfig> {
           const response: AxiosResponse<ApiResponse<EstimationConfig>> = await this.api.post('/estimation/configs', data);
           return response.data.data!;
         }

         async updateEstimationConfig(configId: string, data: Partial<EstimationConfig>): Promise<EstimationConfig> {
           const response: AxiosResponse<ApiResponse<EstimationConfig>> = await this.api.put(`/estimation/configs/${configId}`, data);
           return response.data.data!;
         }

         async deleteEstimationConfig(configId: string): Promise<void> {
           await this.api.delete(`/estimation/configs/${configId}`);
         }

           // Transcription endpoints
  async getJobTranscription(jobId: string): Promise<{ transcribedText: string; originalText: string }> {
    const response: AxiosResponse<ApiResponse<{ transcribedText: string; originalText: string }>> = await this.api.get(`/estimation/jobs/${jobId}/transcription`);
    return response.data.data!;
  }

  async updateJobTranscription(jobId: string, transcribedText: string): Promise<{ transcribedText: string }> {
    const response: AxiosResponse<ApiResponse<{ transcribedText: string }>> = await this.api.put(`/estimation/jobs/${jobId}/transcription`, {
      transcribedText
    });
    return response.data.data!;
  }

  // Estimation calculation endpoints
  async calculateStoryEstimation(storyId: string, configId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/calculate-story', {
      storyId,
      configId
    });
    return response.data.data!;
  }

         async calculateJobEstimation(jobId: string, configId: string): Promise<any> {
           const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/calculate-job', {
             jobId,
             configId
           });
           return response.data.data!;
         }

         async getEstimationResults(jobId: string): Promise<any[]> {
           const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/estimation/results/${jobId}`);
           return response.data.data!;
         }

           async getEstimationAccuracy(configId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/accuracy/${configId}`);
    return response.data.data!;
  }



  // Historical Analytics API methods
  async getAccuracyAnalytics(params: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/analytics/accuracy?${params}`);
    return response.data.data!;
  }

  async getTeamVelocity(teamId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/analytics/velocity/${teamId}`);
    return response.data.data!;
  }

  async getEstimationInsights(params: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/analytics/insights?${params}`);
    return response.data.data!;
  }

  async updateActualEffort(storyId: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/estimation/stories/${storyId}/actual-effort`, data);
    return response.data.data!;
  }

  async getStoryHistory(storyId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/stories/${storyId}/history`);
    return response.data.data!;
  }

  // Estimation Templates API methods
  async getEstimationTemplates(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/estimation/templates');
    return response.data.data!;
  }

  async getEstimationTemplate(templateId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/templates/${templateId}`);
    return response.data.data!;
  }

  async createEstimationTemplate(data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/templates', data);
    return response.data.data!;
  }

  async updateEstimationTemplate(templateId: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/estimation/templates/${templateId}`, data);
    return response.data.data!;
  }

  async deleteEstimationTemplate(templateId: string): Promise<void> {
    await this.api.delete(`/estimation/templates/${templateId}`);
  }

  async getTemplateRecommendations(projectDescription: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/templates/recommendations', { projectDescription });
    return response.data.data!;
  }

  async applyTemplate(templateId: string, config: any, jobId?: string): Promise<ApiResponse<any>> {
    const payload: any = { config };
    if (jobId) {
      payload.jobId = jobId;
    }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/estimation/templates/${templateId}/apply`, payload);
    return response.data;
  }

  async bulkReviewStories(
    jobId: string,
    storyIds: string[],
    status: StoryStatus,
    options?: { rejectionReason?: string; modifications?: string }
  ): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/stories/bulk-review', {
      jobId,
      storyIds,
      status,
      rejectionReason: options?.rejectionReason,
      modifications: options?.modifications
    });
    return response.data;
  }

  async initializeEstimationTemplates(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/templates/initialize');
    // Return the full response data including success, message, created, alreadyExists
    return response.data;
  }

  async getSampleStories(templateType?: string): Promise<any> {
    const params = templateType ? { templateType } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/estimation/sample-stories', { params });
    return response.data.data!;
  }

  async getTemplateSampleStories(templateId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/templates/${templateId}/sample-stories`);
    return response.data.data!;
  }

  // ========== AI/ML Estimation Endpoints ==========

  // Get ML-based story point recommendation
  async getMLRecommendation(data: {
    title: string;
    description?: string;
    estimatedSize?: string;
    complexityFactors?: { frontend: number; backend: number; middleware: number; database: number };
    teamId?: string;
    projectId?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/ml/recommend', data);
    return response.data.data!;
  }

  // Get similar stories
  async getSimilarStories(data: {
    title: string;
    description?: string;
    teamId?: string;
    projectId?: string;
    limit?: number;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/ml/similar-stories', data);
    return response.data.data!;
  }

  // Retrain ML models
  async retrainMLModels(teamId?: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/ml/retrain', { teamId });
    return response.data.data!;
  }

  // ========== Forecasting Endpoints ==========

  // Forecast sprint completion
  async forecastSprintCompletion(sprintId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/forecast/sprint/${sprintId}`);
    return response.data.data!;
  }

  // Forecast project timeline
  async forecastProjectTimeline(projectId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/estimation/forecast/project/${projectId}`);
    return response.data.data!;
  }

  // Calculate effort confidence intervals
  async calculateEffortConfidence(data: {
    storyPoints: number;
    teamId?: string;
    projectId?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/forecast/effort-confidence', data);
    return response.data.data!;
  }

  // ========== Planning Recommendation Endpoints ==========

  // Prioritize backlog
  async prioritizeBacklog(data: {
    projectId: string;
    teamId?: string;
    backlogIssues: Array<{
      id: string;
      title: string;
      description?: string;
      estimatedStoryPoints?: number;
      priority?: string;
      status?: string;
      dependencies?: string[];
      businessValue?: number;
      urgency?: number;
    }>;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/planning/prioritize-backlog', data);
    return response.data.data!;
  }

  // Recommend sprint capacity
  async recommendSprintCapacity(data: {
    teamId: string;
    projectId?: string;
    sprintLength?: number;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/planning/sprint-capacity', data);
    return response.data.data!;
  }

  // Recommend resource allocation
  async recommendResourceAllocation(data: {
    storyId: string;
    teamId: string;
    estimatedStoryPoints?: number;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/estimation/planning/resource-allocation', data);
    return response.data.data!;
  }

  // Custom KPI API methods
  async getCustomKPIs(organizationId?: string): Promise<any[]> {
    const params = organizationId ? { organizationId } : {};
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/custom-kpis', { params });
    return response.data.data || [];
  }

  async getCustomKPI(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/custom-kpis/${id}`);
    return response.data.data!;
  }

  async createCustomKPI(data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/custom-kpis', data);
    return response.data.data!;
  }

  async updateCustomKPI(id: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/custom-kpis/${id}`, data);
    return response.data.data!;
  }

  async deleteCustomKPI(id: string): Promise<void> {
    await this.api.delete(`/custom-kpis/${id}`);
  }

  async calculateKPI(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/custom-kpis/${id}/calculate`);
    return response.data.data!;
  }

  async calculateAllKPIs(organizationId?: string): Promise<any[]> {
    const params = organizationId ? { organizationId } : {};
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/custom-kpis/calculate/all', { params });
    return response.data.data || [];
  }

  async getKPITrends(id: string, days: number = 30): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/custom-kpis/${id}/trends`, {
      params: { days }
    });
    return response.data.data || [];
  }

  async getKPIRecommendations(id: string): Promise<string[]> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.get(`/custom-kpis/${id}/recommendations`);
    return response.data.data || [];
  }

  async initializeSystemKPIs(organizationId?: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.post('/custom-kpis/initialize', { organizationId });
    return response.data.data || [];
  }

  // User Productivity API methods
  async getUserProductivityMetrics(userId: string, dateRange?: { start: Date; end: Date }, weeklyHours?: number): Promise<any> {
    const params: any = {};
    if (dateRange) {
      params.startDate = dateRange.start.toISOString();
      params.endDate = dateRange.end.toISOString();
    }
    if (weeklyHours) {
      params.weeklyHours = weeklyHours.toString();
    }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/user-productivity/users/${userId}`, { params });
    return response.data.data!;
  }

  async getMyProductivityMetrics(dateRange?: { start: Date; end: Date }, weeklyHours?: number): Promise<any> {
    const params: any = {};
    if (dateRange) {
      params.startDate = dateRange.start.toISOString();
      params.endDate = dateRange.end.toISOString();
    }
    if (weeklyHours) {
      params.weeklyHours = weeklyHours.toString();
    }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/user-productivity/me', { params });
    return response.data.data!;
  }

  async compareUserProductivity(dateRange?: { start: Date; end: Date }, organizationId?: string): Promise<any[]> {
    const params: any = {};
    if (dateRange) {
      params.startDate = dateRange.start.toISOString();
      params.endDate = dateRange.end.toISOString();
    }
    if (organizationId) {
      params.organizationId = organizationId;
    }
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/user-productivity/compare', { params });
    return response.data.data || [];
  }

  async getTeamProductivitySummary(teamId?: string, dateRange?: { start: Date; end: Date }): Promise<any> {
    const params: any = {};
    if (dateRange) {
      params.startDate = dateRange.start.toISOString();
      params.endDate = dateRange.end.toISOString();
    }
    if (teamId) {
      params.teamId = teamId;
    }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/user-productivity/team/summary', { params });
    return response.data.data!;
  }
}

export const apiService = new ApiService();
export default apiService;